import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, MoneySchema } from './common';

export const AllergenSchema = z.enum([
  'gluten',
  'lactose',
  'nuts',
  'peanuts',
  'eggs',
  'fish',
  'seafood',
  'soy',
  'sesame',
  'celery',
  'mustard',
  'honey',
]);
export type Allergen = z.infer<typeof AllergenSchema>;

/** Подписи аллергенов для гостя: в интерфейсе — по-русски, в данных — коды. */
export const ALLERGEN_LABELS: Record<Allergen, string> = {
  gluten: 'глютен',
  lactose: 'лактоза',
  nuts: 'орехи',
  peanuts: 'арахис',
  eggs: 'яйцо',
  fish: 'рыба',
  seafood: 'морепродукты',
  soy: 'соя',
  sesame: 'кунжут',
  celery: 'сельдерей',
  mustard: 'горчица',
  honey: 'мёд',
};

export const ModifierOptionSchema = z.object({
  id: IdSchema,
  name: z.string().min(1),
  priceDelta: MoneySchema,
  default: z.boolean().default(false),
  available: z.boolean().default(true),
});
export type ModifierOption = z.infer<typeof ModifierOptionSchema>;

export const ModifierGroupSchema = z.object({
  id: IdSchema,
  name: z.string().min(1),
  min: z.number().int().nonnegative().default(0),
  max: z.number().int().positive().default(1),
  options: z.array(ModifierOptionSchema).min(1),
});
export type ModifierGroup = z.infer<typeof ModifierGroupSchema>;

export const MenuItemSchema = z.object({
  id: IdSchema,
  groupId: IdSchema,
  name: z.string().min(1),
  description: z.string().optional(),
  price: MoneySchema,
  imageUrl: z.string().url().optional(),
  weightGrams: z.number().int().positive().optional(),
  calories: z.number().int().nonnegative().optional(),
  allergens: z.array(AllergenSchema).default([]),
  tags: z.array(z.string()).default([]),
  modifierGroups: z.array(ModifierGroupSchema).default([]),
  /** false, если блюдо в стоп-листе или отключено для канала. */
  available: z.boolean().default(true),
});
export type MenuItem = z.infer<typeof MenuItemSchema>;

export const MenuGroupSchema = z.object({
  id: IdSchema,
  name: z.string().min(1),
  sort: z.number().int().default(0),
  items: z.array(MenuItemSchema),
});
export type MenuGroup = z.infer<typeof MenuGroupSchema>;

/**
 * Снимок меню. Ревизия монотонна: коннектор обязан возвращать новую ревизию
 * при любом изменении состава, цен или модификаторов.
 */
export const MenuSnapshotSchema = z.object({
  venueId: IdSchema,
  revision: z.string().min(1),
  fetchedAt: IsoDateTimeSchema,
  groups: z.array(MenuGroupSchema),
});
export type MenuSnapshot = z.infer<typeof MenuSnapshotSchema>;

export const StopListEntrySchema = z.object({
  itemId: IdSchema,
  reason: z.string().optional(),
  since: IsoDateTimeSchema,
  until: IsoDateTimeSchema.optional(),
});
export type StopListEntry = z.infer<typeof StopListEntrySchema>;

export const StopListSchema = z.object({
  venueId: IdSchema,
  revision: z.string().min(1),
  entries: z.array(StopListEntrySchema),
});
export type StopList = z.infer<typeof StopListSchema>;

export const PaymentTypeSchema = z.object({
  id: IdSchema,
  name: z.string().min(1),
  kind: z.enum(['online', 'on_delivery', 'in_hall', 'bonus']),
});
export type PaymentType = z.infer<typeof PaymentTypeSchema>;

export const ReservationDraftSchema = z.object({
  venueId: IdSchema,
  guestId: IdSchema,
  dateTime: IsoDateTimeSchema,
  guests: z.number().int().positive().max(50),
  tableId: IdSchema.optional(),
  comment: z.string().max(500).optional(),
});
export type ReservationDraft = z.infer<typeof ReservationDraftSchema>;

export const ConnectorReservationSchema = ReservationDraftSchema.extend({
  externalId: IdSchema,
  status: z.enum(['pending', 'confirmed', 'declined', 'cancelled']),
});
export type ConnectorReservation = z.infer<typeof ConnectorReservationSchema>;

export const BonusAccountSchema = z.object({
  phone: z.string().min(10),
  balance: z.number().int().nonnegative(),
  level: z.string().optional(),
});
export type BonusAccount = z.infer<typeof BonusAccountSchema>;
